import { useMemo } from 'react'
import type { MessageGroup } from '../../../../utils/messageGrouping'
import { collectGroupToolCalls, parseToolCallResultCached } from '../../message/tools/tool-call-utils'

export type StagedActionIndex = {
  /** Action id → the most recent `create_staged_action` tool call that produced it. */
  latestStagedActionByActionId: ReadonlyMap<string, string>
  /** Actions whose most recent tool call lives in a turn before the last user message. */
  stagedActionsOwnedByPriorTurn: ReadonlySet<string>
  /** Index of the newest user group, or -1. Hoisted so rows don't rescan per item. */
  lastUserGroupIndex: number
}

/**
 * Scans the grouped conversation once per grouping change to answer the two
 * staged-action ownership questions the rows below need. Kept out of the row
 * render path: it is O(messages × toolCalls), so doing it per row would be
 * quadratic on every streaming token.
 */
export function useStagedActionIndex(messageGroups: Array<MessageGroup>): StagedActionIndex {
  // Iterating oldest→newest means the last entry per action id is the most recent.
  const { latestStagedActionByActionId, stagedActionGroupIndexByActionId } = useMemo(() => {
    const latestByActionId = new Map<string, string>()
    const groupIndexByActionId = new Map<string, number>()
    messageGroups.forEach((group, index) => {
      for (const tc of collectGroupToolCalls(group, { includePrimary: true })) {
        const result = parseToolCallResultCached(tc)
        const actions: Array<{ id: string }> =
          result?.stagedActions ?? (result?.stagedAction ? [result.stagedAction] : [])
        for (const action of actions) {
          latestByActionId.set(action.id, tc.id)
          groupIndexByActionId.set(action.id, index)
        }
      }
    })
    return {
      latestStagedActionByActionId: latestByActionId as ReadonlyMap<string, string>,
      stagedActionGroupIndexByActionId: groupIndexByActionId,
    }
  }, [messageGroups])

  const lastUserGroupIndex = useMemo(() => {
    for (let index = messageGroups.length - 1; index >= 0; index -= 1) {
      if (messageGroups[index].primaryMessage.role === 'user') {
        return index
      }
    }
    return -1
  }, [messageGroups])

  // Actions whose most recent tool call lives in a turn before the last user
  // message already render a completed card from that prior turn. The preview
  // on the latest user message must suppress these to avoid a duplicate card
  // beneath it. Actions created at/after the last user group belong to the
  // active turn, so they stay out of this set and the preview keeps bridging.
  const stagedActionsOwnedByPriorTurn = useMemo(() => {
    const owned = new Set<string>()
    for (const [actionId, groupIndex] of stagedActionGroupIndexByActionId) {
      if (groupIndex < lastUserGroupIndex) owned.add(actionId)
    }
    return owned as ReadonlySet<string>
  }, [stagedActionGroupIndexByActionId, lastUserGroupIndex])

  return useMemo(
    () => ({ latestStagedActionByActionId, stagedActionsOwnedByPriorTurn, lastUserGroupIndex }),
    [latestStagedActionByActionId, stagedActionsOwnedByPriorTurn, lastUserGroupIndex],
  )
}
